import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { coldChain } from '@/lib/trackizy';

const REFRESH_THROTTLE_MS = 5000;
const POLL_MS = 60_000;

/**
 * The Cold Chain overview, kept live: a new fix for any listed device (socket → session.positions)
 * refetches at most once per throttle window; a 60 s poll catches what no position announces (no-data).
 */
export default function useColdChainOverview({ enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // Disabled = the selector returns a constant, so position updates never re-render or re-run effects.
  const positions = useSelector((s) => (enabled ? s.session.positions : null));
  const timer = useRef(null);
  const seen = useRef(new Map());
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++reqId.current;
    try {
      const next = await coldChain.overview();
      if (id !== reqId.current) return; // a newer request is in flight or done — never let an older reply overwrite it
      setData(next);
      setError('');
    } catch (e) {
      if (id === reqId.current) setError(e?.message || 'error');
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, enabled]);

  useEffect(() => {
    if (!enabled || !data?.points?.length) return;
    let changed = false;
    for (const deviceId of new Set(data.points.map((p) => p.deviceId))) {
      const fix = positions?.[deviceId]?.fixTime;
      if (!fix) continue;
      if (seen.current.has(deviceId) && seen.current.get(deviceId) !== fix) changed = true;
      seen.current.set(deviceId, fix);
    }
    if (!changed) return;
    // Throttle, not debounce: a pending refresh is never pushed back, so a device that reports
    // more often than the window still refreshes once per window.
    if (timer.current) return;
    timer.current = setTimeout(() => { timer.current = null; load(); }, REFRESH_THROTTLE_MS);
  }, [positions, data, load, enabled]);

  useEffect(() => () => { clearTimeout(timer.current); timer.current = null; }, []);

  return { data, error, reload: load };
}
